import {
  Box,
  Button,
  Typography,
  IconButton,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { IPatientsTableFilters, usePatientsTable } from './hooks';
import { theme } from '@store';
import { Select, TOOLBAR_FIELD_GAP, toolbarFieldSx } from 'ui';
import { t } from 'i18next';
import {
  Group,
  Close,
  Search,
  FilterAlt,
  SearchOff,
  Refresh,
} from '@mui/icons-material';
import { MultiselectSearchableField } from 'components/Multiselect';
import {
  CareManagementActivitiesModal,
  NoPatientFoundPlaceholder,
  StatusBanner,
} from '@components';
import { ColumnDensityFilter } from 'components/TablePreferences/ColumnDensityFilter';
import {
  PatientTableColumnSelectionFilter,
  PatientTableResetModal,
  TableColumnHeightMapper,
  TableHeaderHeight,
  PinnedColumnMenu,
} from 'components/TablePreferences';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';

interface IPatientsTableProps {
  variant: 'allPatients' | 'myPatients';
  downloadAction?: (filters: IPatientsTableFilters) => void;
}

const CostumeNoResultPlaceholder = () => {
  return (
    <Box
      sx={{
        bgcolor: theme.palette.common.white,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        overflow: 'auto',
        py: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 600,
          alignSelf: 'flex-start',
          [theme.breakpoints.up('lg')]: {
            alignSelf: 'center',
          },
        }}
      >
        <SearchOff sx={{ width: 46, height: 46, mb: 3 }} color='primary' />
        <Typography variant='h6' sx={{ mb: 2 }}>
          {t('patients.searchPatients.resetFilters.title')}
        </Typography>

        <Box sx={{ width: '100%' }}>
          <Typography variant='subtitle2'>
            {t('patients.searchPatients.resetFilters.subTitle')}
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          <NoPatientFoundPlaceholder />
        </Box>

        <StatusBanner
          text={t('patients.searchPatients.resetFilters.note')}
          variant='info'
        />
      </Box>
    </Box>
  );
};

export const PatientsTable = ({ variant }: IPatientsTableProps) => {
  const {
    apiRef,
    pinnedColumns,
    handlePinnedColumnsChange,
    visibilityModel,
    handleUpdateVisibilityModel,
    onDensityChange,
    filters,
    rows,
    columns,
    totalCount,
    loading,
    accountStatusOptions,
    programNameOptions,
    programStatusOptions,
    programTierOptions,
    buildingOptions,
    providerOptions,
    partnersOptions,
    practiceOptions,
    careManagerOptions,
    careNavigatorOptions,
    welnessCoordinatorIds,
    onPageModelChange,
    handleSelectAccountStatus,
    handleSelectProgramTier,
    handleSelectProgramStatus,
    handleSelectProgramName,
    handleSelectCareManagerIds,
    handleSelectCareNavigatorIds,
    handleSelectProviders,
    handleSelectBuildings,
    handleSelectPractices,
    handleSelectWellnessCoordinatorIds,
    handleSearchChange,
    handleResetFilter,
    isFilterDisabled,
    isInitialFilters,
    handleToggleSideFilters,
    isSideFiltersOpen,
    handleSelectPartner,
    applyAdditionalFilters,
    isCMActivitiesModalOpen,
    handleCloseCMActivitiesModal,
    selectedPatientId,
    preferences,
    handleUpdatePreferences,
    handleChangeOrder,
    handleFullReset,
    handleColumnOrderChange,
    handleColumnWidthChange,
    initialSortFilters,
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
  } = usePatientsTable(variant);

  const paginationModel = useMemo(
    () => ({
      page: filters.page - 1,
      pageSize: filters.limit || 100,
    }),
    [filters.page, filters.limit],
  );

  const sortModel = useMemo(
    () =>
      filters.sort_by
        ? [
            {
              field: filters.sort_by,
              sort: (filters.sort_order ?? null) as GridSortDirection,
            },
          ]
        : [],
    [filters.sort_by, filters.sort_order],
  );

  return (
    <>
      {rows.length === 0 && isInitialFilters && !loading ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Group sx={{ width: 56, height: 56, mb: 2 }} color='primary' />
          <Typography variant='h6'>
            {t('patients.searchPatients.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('patients.searchPatients.noResults.subTitle')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isSideFiltersOpen ? '260px 1fr' : '1fr',
            gap: 2,
          }}
        >
          {isSideFiltersOpen && (
            <Box
              sx={{
                width: '260px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FilterAlt />
                    <Typography variant='subtitle1'>
                      {t('patients.searchPatients.filters.filters')}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleToggleSideFilters}>
                    <Close />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    maxHeight: 'calc(100vh - 435px)',
                    overflow: 'auto',
                    pt: 1,
                  }}
                >
                  <Select
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'white',
                    }}
                    name='account_status'
                    size='medium'
                    label={t('patients.searchPatients.filters.status')}
                    value={filters.account_status}
                    onChange={handleSelectAccountStatus}
                    options={accountStatusOptions}
                    clearable
                    clearLabel='All'
                  />

                  <Select
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'white',
                    }}
                    name='program_name'
                    size='medium'
                    label={'Service lines & Program'}
                    value={filters.program_name}
                    onChange={handleSelectProgramName}
                    options={programNameOptions}
                    clearable
                    clearLabel='All'
                  />

                  <Select
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'white',
                    }}
                    name='program_status'
                    size='medium'
                    label={'Program status'}
                    value={filters.program_status}
                    onChange={handleSelectProgramStatus}
                    options={[
                      {
                        value: 'all_aligned_statuses',
                        label: t(
                          'patients.searchPatients.filters.allAlignedStatuses',
                        ),
                      },
                      ...programStatusOptions,
                    ]}
                    clearable
                    clearLabel='All'
                  />

                  <Select
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'white',
                    }}
                    name='program_tier'
                    size='medium'
                    label={'Tier'}
                    value={filters.program_tier}
                    onChange={handleSelectProgramTier}
                    options={programTierOptions}
                    clearable
                    clearLabel='All'
                  />

                  <MultiselectSearchableField
                    options={buildingOptions}
                    value={
                      Array.isArray(filters.building_ids)
                        ? filters?.building_ids
                        : []
                    }
                    name='location'
                    label={t('patients.searchPatients.filters.location')}
                    handleChange={values => handleSelectBuildings(values)}
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />

                  <MultiselectSearchableField
                    options={practiceOptions}
                    value={
                      Array.isArray(filters.practice_ids)
                        ? filters?.practice_ids
                        : []
                    }
                    name='practice'
                    label={t('patients.searchPatients.filters.practice')}
                    handleChange={values => handleSelectPractices(values)}
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />

                  <MultiselectSearchableField
                    options={providerOptions}
                    value={
                      Array.isArray(filters.provider_ids)
                        ? filters?.provider_ids
                        : []
                    }
                    name='provider'
                    label={t('patients.searchPatients.filters.pcp')}
                    handleChange={values => handleSelectProviders(values)}
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />

                  <MultiselectSearchableField
                    options={careManagerOptions}
                    value={
                      Array.isArray(filters.care_manager_ids)
                        ? filters?.care_manager_ids
                        : []
                    }
                    name='care_manager_ids'
                    label={'Care managers'}
                    handleChange={values => handleSelectCareManagerIds(values)}
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />

                  <MultiselectSearchableField
                    options={careNavigatorOptions}
                    value={
                      Array.isArray(filters.care_navigator_ids)
                        ? filters?.care_navigator_ids
                        : []
                    }
                    name='care_navigator_ids'
                    label={'Care navigators'}
                    handleChange={values =>
                      handleSelectCareNavigatorIds(values)
                    }
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />

                  <MultiselectSearchableField
                    options={welnessCoordinatorIds}
                    value={
                      Array.isArray(filters.wellness_coordinator_ids)
                        ? filters?.wellness_coordinator_ids
                        : []
                    }
                    name='wellness_coordinator_ids'
                    label={'Wellness coordinators'}
                    handleChange={values =>
                      handleSelectWellnessCoordinatorIds(values)
                    }
                    size='medium'
                    clearable
                    optional
                    sx={{
                      bgcolor: 'white',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    width: '100%',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    pt: '1rem',
                  }}
                >
                  <Button
                    sx={{ flex: 1 }}
                    variant='outlined'
                    onClick={handleResetFilter}
                    disabled={isFilterDisabled}
                  >
                    {t('patients.searchPatients.filters.reset')}
                  </Button>
                  <Button
                    variant='contained'
                    color='secondary'
                    sx={{ color: 'white', flex: 0.75 }}
                    onClick={applyAdditionalFilters}
                    disabled={isFilterDisabled}
                  >
                    {t('patients.searchPatients.filters.apply')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'auto',
            }}
          >
            <Box
              sx={{
                width: '100%',
                padding: '1rem',
                display: 'flex',
                bgcolor: 'white',
                border: theme => `1px solid ${theme.palette.grey[300]}`,
                borderBottom: 'none',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: TOOLBAR_FIELD_GAP,
                  boxSizing: 'border-box',
                  alignItems: 'center',
                }}
              >
                <OutlinedInput
                  id='searchField'
                  placeholder='Search patient'
                  defaultValue={filters.search}
                  onChange={debounce(handleSearchChange, 500)}
                  sx={toolbarFieldSx}
                  startAdornment={
                    <InputAdornment sx={{ padding: 0 }} position='start'>
                      <Search />
                    </InputAdornment>
                  }
                />
                <Select
                  sx={{
                    ...toolbarFieldSx,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                  name='partner_id'
                  size='medium'
                  label={t('patients.searchPatients.filters.partner')}
                  value={filters.partner_id}
                  onChange={handleSelectPartner}
                  options={partnersOptions}
                  clearable
                  clearLabel='All'
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <PatientTableColumnSelectionFilter
                  hideText={isSideFiltersOpen}
                />

                <ColumnDensityFilter
                  handleDensityChange={density => {
                    handleUpdatePreferences({ density });
                  }}
                  selectedValue={preferences?.density}
                  hideText={isSideFiltersOpen}
                />
                <Button
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    height: '42px',
                    ...(isSideFiltersOpen && {
                      bgcolor: theme.palette.selectedTableRowBg.main,
                    }),
                    fontSize: '15px',
                    fontWeight: 'medium',
                  }}
                  onClick={handleToggleSideFilters}
                >
                  <FilterAlt />
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 'medium',
                      display: {
                        lg: 'initial',
                        md: isSideFiltersOpen ? 'none' : 'initial',
                        sm: 'none',
                      },
                    }}
                  >
                    {t('patients.searchPatients.filters.filters')}
                  </Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 'medium' }}>
                    {filters.appliedFilterCount &&
                    filters.appliedFilterCount > 0
                      ? `(${filters.appliedFilterCount})`
                      : ''}
                  </Typography>
                </Button>
              </Box>
            </Box>
            <Box
              sx={{
                minHeight: rows.length === 0 ? 'calc(100vh - 400px)' : 'unset',
                maxHeight: 'calc(100vh - 400px)',
              }}
            >
              <DataGridPro
                apiRef={apiRef}
                getRowClassName={params =>
                  params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
                }
                sx={{
                  '--DataGrid-overlayHeight': 'calc(100vh - 400px)',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    paddingLeft: 1,
                  },

                  '& .MuiDataGrid-cell': {
                    padding: 0,
                  },
                }}
                slots={{
                  columnMenu: props => (
                    <PinnedColumnMenu
                      {...props}
                      pinnedColumns={pinnedColumns}
                    />
                  ),
                  pagination: props => (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                      }}
                    >
                      <Button
                        color='primary'
                        sx={{ whiteSpace: 'nowrap' }}
                        startIcon={<Refresh color='primary' />}
                        onClick={handleOpenResetViewModal}
                      >
                        RESET VIEW
                      </Button>
                      <GridPagination {...props} />
                    </Box>
                  ),
                  noRowsOverlay: CostumeNoResultPlaceholder,
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: false,
                    printOptions: { disableToolbarButton: true },
                    csvOptions: { disableToolbarButton: true },
                  },
                  row: {
                    style: {
                      cursor: 'pointer',
                    },
                  },
                }}
                disableColumnFilter
                pageSizeOptions={[10, 25, 50, 100]}
                pagination
                paginationModel={paginationModel}
                rows={rows}
                columns={columns}
                rowCount={totalCount}
                disableMultipleColumnsSorting
                filterMode='server'
                sortingMode='server'
                paginationMode='server'
                pinnedColumns={pinnedColumns}
                onPinnedColumnsChange={handlePinnedColumnsChange}
                columnVisibilityModel={visibilityModel}
                columnHeaderHeight={TableHeaderHeight}
                getRowHeight={() =>
                  TableColumnHeightMapper[preferences?.density ?? 'comfortable']
                }
                onColumnOrderChange={handleColumnOrderChange}
                onColumnWidthChange={handleColumnWidthChange}
                onColumnVisibilityModelChange={handleUpdateVisibilityModel}
                onDensityChange={onDensityChange}
                onPaginationModelChange={model => {
                  onPageModelChange(model);
                }}
                sortModel={sortModel}
                onSortModelChange={model => {
                  if (
                    model[0] &&
                    (model[0].field !== filters.sort_by ||
                      model[0].sort !== filters.sort_order)
                  ) {
                    handleChangeOrder(model[0].field, model[0].sort);
                  } else {
                    handleChangeOrder(
                      initialSortFilters.sort_by,
                      initialSortFilters.sort_order as GridSortDirection,
                    );
                  }
                }}
                loading={loading}
              />
            </Box>
          </Box>
        </Box>
      )}
      <PatientTableResetModal
        onResetAction={handleFullReset}
        isOpen={isResetViewModalOpen}
        onClose={handleCloseResetViewModal}
      />
      <CareManagementActivitiesModal
        onClose={handleCloseCMActivitiesModal}
        isOpen={isCMActivitiesModalOpen}
        patient_id={selectedPatientId}
        isLoading={false}
        confirmAction={handleCloseCMActivitiesModal}
        variant='view'
      />
    </>
  );
};
